import { useState, type FormEvent } from "react";
import { gradeLabel, isGrade } from "@vavr/core";
import { ActionTile, Avatar, Badge, Banner, Button, Card, Dialog, DialogClose, EmptyState, Icon, ListGroup, ListRow, Spinner, Switch, TextField, useToast } from "@vavr/ui";
import { api, ApiError } from "../shared/api.ts";
import { Link, navigate } from "../shared/router.tsx";
import { useAsync } from "../shared/useAsync.ts";
import { day, fullName } from "../shared/format.ts";
import { G, useGuardian } from "./context.tsx";
import type { GuardianChild, GuardianSite } from "./status.ts";

export interface LinkedAdult {
  id: string; first: string; last: string; photo_sha256: string | null; relation: string;
  kind: "guardian" | "authorised" | "delegate"; valid_from: number | null; valid_to: number | null;
}
export interface Delegation { id: string; child_ids: string[]; delegate_adult_id: string; valid_from: number; valid_to: number; status: string; used_seq: number | null }

const KIND_ORDER = { guardian: 0, authorised: 1, delegate: 2 } as const;
const KIND_LABEL = { guardian: "Guardian", authorised: "Can collect", delegate: "One-time" } as const;

export function windowText(a: { valid_from: number | null; valid_to: number | null }, tz: string, now = Date.now()): string | null {
  if (a.valid_to != null && a.valid_to < now) return "Expired";
  if (a.valid_from != null && a.valid_from > now) return `From ${day(a.valid_from, tz)}${a.valid_to ? ` to ${day(a.valid_to, tz)}` : ""}`;
  if (a.valid_to != null) return `Until ${day(a.valid_to, tz)}`;
  return null;
}

export function AdultsView({ site, child, adults, onRemove }: { site: GuardianSite["site"]; child: GuardianChild; adults: LinkedAdult[]; onRemove?: (a: LinkedAdult) => void }) {
  return (
    <div className="pmu-g-group">
      <ListGroup
        id={`adults-${child.id}`} title={`Who can collect ${child.first}`} description="The door checks this list at every pickup."
        action={<Link className="pmu-g-chip pmu-g-chip--brand" href={`${G}/people/${site.id}/${child.id}/add`}><Icon name="plus" />Add</Link>}
      >
        {adults.length === 0 ? <ListRow title="No one yet" subtitle="Add the adults who may collect." /> : (
          [...adults].sort((x, y) => KIND_ORDER[x.kind] - KIND_ORDER[y.kind]).map((a) => {
            const w = windowText(a, site.tz);
            return (
              <ListRow
                key={a.id}
                leading={<Avatar kind="adult" name={fullName(a)} />}
                title={<>{fullName(a)} <Badge tone={a.kind === "guardian" ? "brand" : "neutral"}>{KIND_LABEL[a.kind]}</Badge></>}
                subtitle={`${a.relation}${w ? ` · ${w}` : ""}`}
                trailing={a.kind !== "guardian" && onRemove ? (
                  <Dialog
                    title={`Remove ${a.first}?`}
                    description={`${fullName(a)} will no longer be able to collect ${child.first}. Past records stay.`}
                    trigger={<Button size="sm" variant="ghost" aria-label={`Remove ${fullName(a)}`}>Remove</Button>}
                    actions={<><DialogClose><Button variant="secondary">Keep</Button></DialogClose><DialogClose><Button variant="danger" onClick={() => onRemove(a)}>Remove</Button></DialogClose></>}
                  />
                ) : undefined}
              />
            );
          })
        )}
      </ListGroup>
      <p className="pmu-small pmu-muted pmu-g-footnote">To change a guardian, ask the office. Custody changes need their paperwork.</p>
    </div>
  );
}

function ChildAdults({ s, child }: { s: GuardianSite; child: GuardianChild }) {
  const toast = useToast();
  const { version } = useGuardian();
  const q = useAsync(() => api<{ adults: LinkedAdult[] }>("GET", `/api/v1/guardian/sites/${s.site.id}/children/${child.id}/adults`), [s.site.id, child.id, version]);
  if (q.loading && !q.data) return <Spinner label={`Loading adults for ${child.first}`} />;
  if (q.error) return <Banner tone="danger" title={q.error.message} />;
  return (
    <>
      <AdultsView site={s.site} child={child} adults={q.data!.adults} onRemove={async (a) => {
        try {
          await api("DELETE", `/api/v1/guardian/sites/${s.site.id}/children/${child.id}/adults/${a.id}`);
          toast.show({ title: `${a.first} removed`, tone: "success" });
          q.reload();
        } catch (e) { toast.show({ title: (e as ApiError).message, tone: "danger" }); }
      }} />
      <GoingHome siteId={s.site.id} child={child} />
    </>
  );
}

interface GoingHomeInfo {
  grade: string | null;
  home_alone: { allowed: boolean; from: string | null; consent: boolean };
  sibling_from: string | null;
  siblings: { id: string; first: string; allowed: boolean; consent: boolean }[];
}

const from = (g: string | null) => (isGrade(g) ? gradeLabel(g) : null);

/**
 * Leaving school without an adult (school rules): the parent's consents, within what the school
 * allows for the child's grade (going home alone) and the sibling's (being collected by them).
 */
function GoingHome({ siteId, child }: { siteId: string; child: GuardianChild }) {
  const toast = useToast();
  const q = useAsync(() => api<GoingHomeInfo>("GET", `/api/v1/guardian/sites/${siteId}/children/${child.id}/going-home`), [siteId, child.id]);
  const [busy, setBusy] = useState<string | null>(null);
  const d = q.data;
  if (!d) return null;
  // Nothing the school allows and nothing already given: no section.
  if (!d.home_alone.from && !d.home_alone.consent && !(d.sibling_from && d.siblings.length) && !d.siblings.some((x) => x.consent)) return null;
  const set = async (key: string, body: Record<string, unknown>, ok: string) => {
    setBusy(key);
    try { await api("PUT", `/api/v1/guardian/sites/${siteId}/children/${child.id}/consents`, body); toast.show({ title: ok, tone: "success" }); q.reload(); }
    catch (e) { toast.show({ title: (e as ApiError).message, tone: "danger" }); }
    finally { setBusy(null); }
  };
  const aloneHint = d.home_alone.allowed ? "Walking or biking home. Their teacher marks it, and you get a notification."
    : d.home_alone.from ? `Your school allows this from ${from(d.home_alone.from)}${d.grade ? "" : ", and has not set " + child.first + "'s grade yet"}.` : "Your school does not allow children to go home on their own.";
  return (
    <Card title={`${child.first} going home without an adult`} description="The school checks this before letting them leave. You can change it at any time.">
      <div className="pmu-stack">
        <Switch label={`${child.first} may go home on their own`} description={aloneHint}
          checked={d.home_alone.consent} disabled={busy !== null || (!d.home_alone.allowed && !d.home_alone.consent)}
          onCheckedChange={(on) => void set("alone", { kind: "home_alone", on }, on ? `${child.first} may go home on their own` : "Consent withdrawn")} />
        {d.siblings.map((x) => (
          <Switch key={x.id} label={`${child.first} may leave with ${x.first}`}
            description={x.allowed ? `${x.first} can collect ${child.first} at the end of the day.` : d.sibling_from ? `Your school allows this when the brother or sister is in ${from(d.sibling_from)} or above.` : "Your school does not allow brothers or sisters to collect."}
            checked={x.consent} disabled={busy !== null || (!x.allowed && !x.consent)}
            onCheckedChange={(on) => void set(x.id, { kind: "sibling", sibling_child_id: x.id, on }, on ? `${x.first} can collect ${child.first}` : "Consent withdrawn")} />
        ))}
      </div>
    </Card>
  );
}

export function People() {
  const { overview } = useGuardian();
  if (!overview.sites.length) return <EmptyState icon="user" title="No children linked yet" />;
  return (
    <div className="pmu-stack pmu-g-people">
      <ActionTile layout="row" tone="primary" icon="phone" title="Send a one-time code" description="Someone else picking up today? They need no app." onClick={() => navigate(`${G}/delegate`)} />
      {overview.sites.flatMap((s) => s.children.map((c) => <ChildAdults key={`${s.site.id}:${c.id}`} s={s} child={c} />))}
    </div>
  );
}

const toMs = (d: string) => (d ? new Date(`${d}T00:00:00`).getTime() : null);

export function AddAdult({ siteId, childId }: { siteId: string; childId: string }) {
  const { overview } = useGuardian();
  const s = overview.sites.find((x) => x.site.id === siteId);
  const child = s?.children.find((c) => c.id === childId);
  const [f, setF] = useState({ first: "", last: "", relation: "", phone: "", from: "", to: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  if (!s || !child) return <EmptyState title="Not found" />;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const from = toMs(f.from);
      // "To" is inclusive: the adult may collect until the end of that day.
      const to = f.to ? toMs(f.to)! + 86_399_000 : null;
      if (from != null && to != null && to <= from) throw new ApiError(400, "window", "The end date must be after the start date.");
      await api("POST", `/api/v1/guardian/sites/${siteId}/authorised`, {
        first: f.first, last: f.last, relation: f.relation, phone: f.phone ? normalisePhone(f.phone) : null, child_ids: [childId], valid_from: from, valid_to: to,
      });
      toast.show({ title: `${f.first} can now collect ${child!.first}`, tone: "success" });
      navigate(`${G}/people`);
    } catch (err) {
      setError((err as ApiError).message);
    } finally { setBusy(false); }
  }

  return (
    <form className="pmu-stack pmu-g-form" onSubmit={submit}>
      <h2 className="pmu-g-h2">Add someone who can collect {child.first}</h2>
      {error && <Banner tone="danger" title={error} />}
      <div className="pmu-grid-2">
        <TextField label="First name" required autoComplete="off" value={f.first} onChange={set("first")} />
        <TextField label="Last name" required autoComplete="off" value={f.last} onChange={set("last")} />
      </div>
      <TextField label="Relationship" required placeholder="Grandfather, neighbor, nanny" value={f.relation} onChange={set("relation")} />
      <TextField label="Mobile phone (optional)" type="tel" inputMode="tel" autoComplete="off" value={f.phone} onChange={set("phone")} hint="US number. Used only if the school needs to reach them." />
      <div className="pmu-grid-2">
        <TextField label="Starting (optional)" type="date" value={f.from} onChange={set("from")} />
        <TextField label="Until (optional)" type="date" value={f.to} onChange={set("to")} hint="Leave blank for no end date." />
      </div>
      <p className="pmu-small pmu-muted">Staff compare this person with a photo taken at the door. Vavr never uses facial recognition.</p>
      <Button type="submit" loading={busy} fullWidth>Add {f.first || "this person"}</Button>
      <Button variant="ghost" onClick={() => navigate(`${G}/people`)} fullWidth>Cancel</Button>
    </form>
  );
}

/** "(512) 555-0123" → "+15125550123"; anything else is sent as typed for the server to judge. */
export function normalisePhone(input: string): string {
  const d = input.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return input.trim();
}
